import { invert } from 'lodash';
import { PosologiaEstruturada } from './prescription';

export enum OriginEnum {
  'MEDFLOW' = 'MEDFLOW',
}

export interface MedicineIcd {
  prescriptionMedicineIcdId?: number;
  prescriptionMedicineId?: number;
  icd10Id: number;
  icd10: string;
  icd?: {
    id: number;
    subcategoria: string;
    descricao: string;
  };
}

export interface ActivePrinciple {
  Nome: string;
  CAS: string;
}

export interface Medicine {
  idPrescricaoMedicamento: number;
  Posologia: string;
  Quantidade: number | null;
  UsoContinuo: boolean;
  LaboratorioPreferencia: number;
  PermitirTroca: number;
  FarmaciaPopular: boolean;
  AtivoAnvisa: boolean;
  ViaAdministracao: string;
  ImpressaoDuasVias: boolean;
  Dispensado: boolean;
  QuantidadeDispensada: number | null;
  ListaEANS: any[];
  EANs?: string[];
  DetalhesDispensa: any[];
  IdMedicamento: number;
  Nome: string;
  Concentracao: string;
  Unidade: string;
  Farmacos: string[];
  PrincipiosAtivos: ActivePrinciple[];
  Grupo?: string;
  TipoMedicamento: string;
  TipoMedicamentoComAcentuacao: string;
  FormaFarmaceutica: string;
  FormaFarmaceuticaComAcentuacao: string;
  UsoInterno: boolean;
  Antimicrobiano: boolean;
  idTipoAntimicrobiano: number;
  AntimicrobianoDescricao: string;
  Desconto: number;
  PodeDispensaEletronica: boolean;
  idPortaria344: number | string;
  CodigoATC: string;
  ControleEspecial: boolean;
  MIP: boolean;
  TemBeneficio: boolean;
  Label: string;
  LabelFarmaco: string;
  Portaria344?: string;
  MenorPreco?: number;
  MaiorPreco?: number;
  Embalagens: Array<string>;
  Embalagem?: number | null | string;
  Laboratorio?: string | null;
  Laboratorios?: Array<string>;
  LaboratoriosDisponiveis?: Array<LaboratorioOption>;
  idLaboratorioSelecionado?: number | null;
  idLaboratorioPatrocinado?: number | null;
  IdViaAdministracao: number;
  DataAdicao?: string;
  LME: boolean;
  medicamentoLme?: SRMICDUnitByMonth;
  ProdutoCorrelato: boolean;
  Beneficios: PSPBenefit[];
  PossuiRename?: boolean;
  PossuiRemume?: boolean;
  QuantidadeUnidadeFormatada: string;
  Ordem: number | null;
  Origem?: OriginEnum;
  EmbalagensEstruturadas: StructuredPacking[];
  EmbalagemEstruturada: StructuredPacking;
  ParanaSemDor: boolean;
  EmFalta?: boolean;
  isPremiumPlacement?: boolean;
  ctaText?: string;
  ctaLink?: string;
  isIncluddedInPharmaModels?: boolean;
  posologySelectedFromSuggestion?: boolean;
  posologySelectedFromSuggestionId?: number | null;
  isStructuredPosology?: boolean;
  hasConcomitance?: boolean;
  ConcomitanceMedicines?: Medicine[];
  comercialCategory?: string;
  startDate?: string;
  endDate?: string;
  pharmacoLabel?: string;
  medicineDescription?: string;
  AdicionadoPorIA?: boolean;
  isHighlighted?: boolean;
  pspInitiated?: boolean;
  pspProgramData?: PspProgramData | null;
  pbmConsentGiven?: boolean;
  pbmRescueDismissed?: boolean;
  pbmProgramData?: PspProgramData | null;
  QuantidadeRecorrencia?: number | null;
  PosologiaEstruturada?: PosologiaEstruturada;
  medicineIcd?: MedicineIcd;
}

export interface LaboratorioOption {
  id: number;
  nome: string;
}

export interface GenericLabPreference {
  id: number;
  medicineId: number;
  laboratoryId: number;
  active: boolean;
}

export interface GenericLabPreferenceProduct {
  productType: 'generic_lab_preference';
  genericLabPreference: GenericLabPreference;
}

export interface GenericLabPreferenceCampaign {
  id: number;
  name: string;
  active: boolean;
  products: GenericLabPreferenceProduct[];
}

export interface CampaignProduct {
  id: number;
  active: boolean;
  name: string;
  products: ConcomitantProduct[];
}

export interface ConcomitantProduct {
  concomitance: ConcomitantRecord;
  productType: 'concomitance';
}

export interface ConcomitantRecord {
  id: number;
  primaryItemId: number;
  concomitantMedicineIds: number[];
  concomitantMedicine: Medicine[];
  type: 'medicine';
  active: boolean;
  isHighlighted?: boolean;
}

export interface StructuredPacking {
  Embalagem: string;
  LabelEmbalagem: string;
  VolumeEmbalagem: number;
  QuantidadeEmbalagens: number;
  UnidadeVolumeEmbalagem: string;
  TipoEmbalagem: string;
  PreSelecionado?: boolean;
}

export enum BENEFIT_TYPE {
  FARMACIA_POPULAR = 'FARMACIA_POPULAR',
  PARCEIRO = 'PARCEIRO',
  RENAME = 'RENAME',
  REMUME = 'REMUME',
  INDUSTRIA = 'INDUSTRIA',
}

export interface PSPBenefit {
  Tipo: BENEFIT_TYPE;
  Nome: string;
  Logo: string;
  CorFundo: string;
  Descricao: string;
  CorTexto: string;
  PermitirFiltroNaBusca: boolean;
  Descontos: Discounts[];
  DescontosLabel: string;
}

export function getPspBenefit(beneficios?: PSPBenefit[]): PSPBenefit | undefined {
  return beneficios?.find((b) => b.Tipo === BENEFIT_TYPE.INDUSTRIA && b.Descricao?.startsWith?.('PSP'));
}

export type PbmBenefit = PSPBenefit;

export interface PbmConsentData {
  consentGiven: boolean;
  recordedAt?: string;
}

export function getPbmBenefit(beneficios?: PSPBenefit[]): PbmBenefit | undefined {
  return beneficios?.find((b) => b.Tipo === BENEFIT_TYPE.INDUSTRIA && b.Descricao?.startsWith?.('PBM'));
}

export interface PspProgramMedicine {
  id: number;
  pspProgramId: number;
  medicineId: number | null;
  eanCode: string;
  medicineName: string;
  patientConsent: boolean;
  doctorConsent: boolean;
}

export interface PspProgramLookup {
  id: number;
  name: string;
  pharmaceutical: string;
  partner: string;
  programType: string;
  optoutUrl: string;
  partnerProgramUrl: string;
  shortDescription: string;
  consentTermVersion: string;
  active: boolean;
  medicines: PspProgramMedicine[];
}

export interface PspCpfLookupResponse {
  registered: boolean;
  details: string | null;
  program: PspProgramLookup | null;
}

export interface PspProgramData {
  program?: PspProgramLookup | null;
  registered?: boolean;
  details?: string | null;
}

export interface Discounts {
  Unidades: string;
  PercentualDesconto?: number;
}

export interface UnitsPerMonth {
  unidade: number;
  mes: number;
}

export interface SRMICDUnitByMonth {
  cid: string;
  unidades: Array<UnitsPerMonth>;
  paranaSemDor?: boolean;
}

export interface ManualMedicine {
  idPrescricaoMedicamentoManual: number | null;
  idTipoAntimicrobiano?: number | null;
  idMedicamentoManual: number | null;
  Quantidade: number | null;
  UsoContinuo: boolean;
  UsoInterno: boolean;
  Nome: string;
  Dispensado: boolean;
  QuantidadeDispensada: number | null;
  Label: string;
  DetalhesDispensa: any[];
  Manual: boolean;
  Posologia: string;
  Embalagem: number | null | string;
  DataAdicao?: string;
  LME: boolean;
  ProdutoCorrelato: boolean;
  QuantidadeUnidadeFormatada?: string;
  Ordem: number | null;
  Origem?: OriginEnum;
  TipoReceita: PT_PRESCRIPTION_TYPE | null;
  Manipulado: boolean;
  isIncluddedInPharmaModels?: boolean;
  AdicionadoPorIA?: boolean;
  EANs?: string[];
  Beneficios?: PSPBenefit[];
  QuantidadeRecorrencia?: number | null;
  PosologiaEstruturada?: PosologiaEstruturada;
}

export enum PRESCRIPTION_TYPE {
  'SIMPLE' = 'SIMPLE',
  'SIMPLE_IN_TWO_WAYS' = 'SIMPLE_IN_TWO_WAYS',
  'CONTROLLED' = 'CONTROLLED',
  'COMPOUNDED_SIMPLE' = 'COMPOUNDED_SIMPLE',
  'COMPOUNDED_SIMPLE_IN_TWO_WAYS' = 'COMPOUNDED_SIMPLE_IN_TWO_WAYS',
  'COMPOUNDED_CONTROLLED' = 'COMPOUNDED_CONTROLLED',
  'OTHER' = 'OTHER',
}

export enum PT_PRESCRIPTION_TYPE {
  'SIMPLES' = 'SIMPLES',
  'SIMPLES_DUAS_VIAS' = 'SIMPLES_DUAS_VIAS',
  'CONTROLADO' = 'CONTROLADO',
  'MANIPULADO_SIMPLES' = 'MANIPULADO_SIMPLES',
  'MANIPULADO_DUAS_VIAS' = 'MANIPULADO_DUAS_VIAS',
  'MANIPULADO_CONTROLADO' = 'MANIPULADO_CONTROLADO',
  'OUTRO' = 'OUTRO',
}

export const PRESCRIPTION_TYPE_MAP = {
  [PRESCRIPTION_TYPE.SIMPLE]: PT_PRESCRIPTION_TYPE.SIMPLES,
  [PRESCRIPTION_TYPE.SIMPLE_IN_TWO_WAYS]: PT_PRESCRIPTION_TYPE.SIMPLES_DUAS_VIAS,
  [PRESCRIPTION_TYPE.CONTROLLED]: PT_PRESCRIPTION_TYPE.CONTROLADO,
  [PRESCRIPTION_TYPE.COMPOUNDED_SIMPLE]: PT_PRESCRIPTION_TYPE.MANIPULADO_SIMPLES,
  [PRESCRIPTION_TYPE.COMPOUNDED_SIMPLE_IN_TWO_WAYS]: PT_PRESCRIPTION_TYPE.MANIPULADO_DUAS_VIAS,
  [PRESCRIPTION_TYPE.COMPOUNDED_CONTROLLED]: PT_PRESCRIPTION_TYPE.MANIPULADO_CONTROLADO,
  [PRESCRIPTION_TYPE.OTHER]: PT_PRESCRIPTION_TYPE.OUTRO,
};

export const MEDICINE_TYPE_OPTIONS: { label: string; value: PRESCRIPTION_TYPE }[] = [
  { label: 'Receituário Simples', value: PRESCRIPTION_TYPE.SIMPLE },
  {
    label: 'Receituário Simples em Duas Vias',
    value: PRESCRIPTION_TYPE.SIMPLE_IN_TWO_WAYS,
  },
  { label: 'Receituário Controlado', value: PRESCRIPTION_TYPE.CONTROLLED },
  { label: 'Outro', value: PRESCRIPTION_TYPE.OTHER },
];

export const MEDICINE_TYPE_OPTIONS_MAP = {
  [PRESCRIPTION_TYPE.SIMPLE]: 'Receituário Simples',
  [PRESCRIPTION_TYPE.SIMPLE_IN_TWO_WAYS]: 'Receituário Simples em Duas Vias',
  [PRESCRIPTION_TYPE.CONTROLLED]: 'Receituário Controlado',
  [PRESCRIPTION_TYPE.OTHER]: 'Outro',
  [PRESCRIPTION_TYPE.COMPOUNDED_SIMPLE]: 'Receituário Simples',
  [PRESCRIPTION_TYPE.COMPOUNDED_SIMPLE_IN_TWO_WAYS]: 'Receituário Simples em Duas Vias',
  [PRESCRIPTION_TYPE.COMPOUNDED_CONTROLLED]: 'Receituário Controlado',
};

export const MEDICINE_TYPE_OPTIONS_MAP_FORMULA = {
  [PRESCRIPTION_TYPE.SIMPLE]: 'Receituário Simples',
  [PRESCRIPTION_TYPE.SIMPLE_IN_TWO_WAYS]: 'Receituário Simples em Duas Vias',
  [PRESCRIPTION_TYPE.CONTROLLED]: 'Receituário Controlado',
  [PRESCRIPTION_TYPE.OTHER]: 'Outro',
};

export function translatePrescriptionTypeToPt(type: PRESCRIPTION_TYPE | null): PT_PRESCRIPTION_TYPE | null {
  if (type === null) return null;
  return PRESCRIPTION_TYPE_MAP[type];
}

export function translatePrescriptionTypeToEn(type: PT_PRESCRIPTION_TYPE | null): PRESCRIPTION_TYPE | null {
  if (type === null) return null;
  const inverted = invert(PRESCRIPTION_TYPE_MAP) as Record<PT_PRESCRIPTION_TYPE, PRESCRIPTION_TYPE>;
  return inverted[type];
}
